import { CheckCircle, Lightbulb, Target, AlertTriangle, Star } from 'lucide-react';
import type { RealTimeExtraction, RealTimeExtractionType } from '../../types';

const TYPE_CONFIG: Record<RealTimeExtractionType, { icon: typeof CheckCircle; label: string; color: string }> = {
  action_item: { icon: CheckCircle, label: 'Action', color: 'var(--color-success)' },
  key_insight: { icon: Lightbulb, label: 'Insight', color: 'var(--color-brand-accent)' },
  goal: { icon: Target, label: 'Goal', color: 'var(--flank-teal)' },
  contradiction: { icon: AlertTriangle, label: 'Tension', color: 'var(--color-warning)' },
  value_stated: { icon: Star, label: 'Value', color: 'var(--color-brand-energy)' },
};

interface ExtractionPanelProps {
  extractions: RealTimeExtraction[];
  isEnding?: boolean;
}

export default function ExtractionPanel({ extractions, isEnding }: ExtractionPanelProps) {
  if (extractions.length === 0 && !isEnding) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
          style={{ background: 'var(--color-surface)' }}
        >
          <Lightbulb size={18} style={{ color: 'var(--color-text-muted)' }} />
        </div>
        <p className="text-sm text-[var(--color-text-muted)]">
          Key insights will appear here as the conversation unfolds
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3 overflow-y-auto">
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] px-1 mb-1">
        Captured
      </p>

      {extractions.map((item, i) => {
        const config = TYPE_CONFIG[item.type];
        const Icon = config.icon;

        return (
          <div
            key={i}
            className="flex items-start gap-2.5 p-2.5 rounded-lg animate-fade-in"
            style={{ background: 'var(--color-surface)' }}
          >
            <Icon
              size={14}
              className="mt-0.5 shrink-0"
              style={{ color: config.color }}
            />
            <div className="min-w-0">
              <span
                className="text-xs font-medium mr-1.5"
                style={{ color: config.color }}
              >
                {config.label}
              </span>
              <span className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
                {item.content}
              </span>
            </div>
          </div>
        );
      })}

      {isEnding && (
        <div
          className="flex items-center gap-2.5 p-2.5 rounded-lg mt-2"
          style={{ background: 'var(--color-success-subtle)' }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0 mt-0.5"
            style={{ background: 'var(--color-success)' }}
          />
          <span className="text-xs text-[var(--color-text-secondary)]">
            Processing session…
          </span>
        </div>
      )}
    </div>
  );
}
